import { reactive } from 'vue';

import { endOfDayInTimezone, getMaxUploadFileSizeBytes, initialState, startOfDayInTimezone } from '@/Helpers.js';
import moment from '@/plugins/vue-moment.js';
import store from '@/store/Index.js';
import { buildAccessDeniedLocation } from '@/utilities/accessDeniedLocation.js';
import Util from '@/utilities/Utilities.js';

/** Matches Bulma `$desktop` / app navbar breakpoint (see `layouts/AppBreadcrumb`). */
const VIEWPORT_DESKTOP_MIN_PX = 1024;

const state = reactive({
    windowWidth: 0,
});

const utilities = {
    install: (app, { router, $i18n }) => {
        app.config.globalProperties.$databaseFormat = 'YYYY-MM-DD HH:mm:ss';

        app.config.globalProperties.$validStep = 0.25;

        app.config.globalProperties.$hasFeature = feature => {
            const { user } = store.state.Auth;

            const featureClass = store.state?.features?.[feature];

            return user.hasActiveFeature(featureClass);
        };

        // Enforce the user-facing display limit on file selection (not the larger server MaxFileSize).
        const maxFileSizeLabel = initialState('MaxFileSizeDisplay') || '20 MB';
        const maxFileSize = getMaxUploadFileSizeBytes(maxFileSizeLabel);
        app.config.globalProperties.globalMaxFileSize = maxFileSize;
        app.config.globalProperties.globalMaxFileSizeLabel = maxFileSizeLabel;

        const locale = initialState('InstitutionSettings.locale', 'en-US');

        Object.defineProperties(app.config.globalProperties, {
            $timezone: {
                get() {
                    // Get current institution timezone from window.config or fallback
                    const currentTimezone =
                        window.config?.institution?.timezone ||
                        initialState('InstitutionSettings.timezone', 'America/Los_Angeles');
                    return moment.tz.zone(currentTimezone);
                },
            },

            $locale: {
                get() {
                    return locale;
                },
            },

            $tzOffset: {
                get() {
                    const browserOffset = this.$moment.tz(this.$moment.tz.guess()).utcOffset();
                    const institutionOffset = this.$moment.tz(this.$timezone.name).utcOffset();

                    return institutionOffset - browserOffset;
                },
            },

            $isTablet: {
                get() {
                    return state.windowWidth < 1200 && state.windowWidth > 768;
                },
            },

            $isMobile: {
                get() {
                    return state.windowWidth <= 768;
                },
            },

            $isDesktopViewport: {
                get() {
                    return state.windowWidth >= VIEWPORT_DESKTOP_MIN_PX;
                },
            },
        });

        app.config.globalProperties.setWindowWidth = function () {
            state.windowWidth = window.innerWidth;
        };

        /**
         *
         * @param date
         * @param format
         * @param fromNow
         * @returns {*}
         */
        app.config.globalProperties.formatDate = function (date, format, fromNow) {
            const dateString = date instanceof moment ? date.toDate() : date;

            const momentDate = moment.utc(dateString).tz(this.$timezone.name);

            return fromNow ? momentDate.fromNow() : momentDate.format(format);
        };

        /**
         *
         * @param date
         * @param timezone
         * @returns {*}
         */
        app.config.globalProperties.forceTimezone = function (date, timezone) {
            const strippedDate = moment.tz(date, moment.tz.guess()).format(app.config.globalProperties.$databaseFormat);

            return moment.tz(strippedDate, timezone);
        };

        /**
         * Get the start of day (midnight) for a given date in the specified timezone.
         * Uses the JavaScript Date's local date components to avoid timezone conversion issues.
         *
         * @param {Date} date - JavaScript Date object (from date picker, in browser's local time)
         * @param {string} timezone - Target timezone (defaults to institution timezone)
         */
        app.config.globalProperties.startOfDay = function (date, timezone) {
            return startOfDayInTimezone(
                date,
                timezone || this.$timezone.name,
                app.config.globalProperties.$databaseFormat
            );
        };

        /**
         * Get the end of day (23:59:59) for a given date in the specified timezone.
         * Uses the JavaScript Date's local date components to avoid timezone conversion issues.
         *
         * @param {Date} date - JavaScript Date object (from date picker, in browser's local time)
         * @param {string} timezone - Target timezone (defaults to institution timezone)
         */
        app.config.globalProperties.endOfDay = function (date, timezone) {
            return endOfDayInTimezone(
                date,
                timezone || this.$timezone.name,
                app.config.globalProperties.$databaseFormat
            );
        };

        /**
         *
         * @param string
         * @param length
         * @returns {string}
         */
        app.config.globalProperties.truncate = function (string, length) {
            return Util.truncate(string, length, '...');
        };

        app.config.globalProperties.redirectToAccessDenied = function (intended = null) {
            router.push(buildAccessDeniedLocation(intended || router.currentRoute.value));
        };

        /**
         *
         * @param action
         * @param subject
         * @param redirect
         * @returns {boolean}
         */
        app.config.globalProperties.authorize = function (action, subject, redirect = null) {
            if (!app.config.globalProperties.$gate.allows(action, subject)) {
                if (redirect) {
                    redirect();

                    return false;
                }

                app.config.globalProperties.redirectToAccessDenied();

                return false;
            }

            return true;
        };

        app.config.globalProperties.$systemAlertHeight = 35;
    },
};

export default utilities;
